<template>
  <button
    :type="buttonType"
    :class="buttonClass + themeClassName"
    :data-qa="dataQa"
    :disabled="disabled || null"
    @click="$emit('click', $event)"
  >
    <slot />
  </button>
</template>

<script>
import { getBrandName } from '@/helpers/BrandHelper'

export default {
  props: {
    buttonClass: {
      type: String,
      default: 'primary',
    },
    buttonType: {
      type: String,
      default: 'button',
    },
    dataQa: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    themeClassName() {
      return ` theme-${getBrandName()}`
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:color'; //already imported via vite.config.mjs but also here to keep IDE happy

button {
  border: none;
  cursor: pointer;
  display: inline-block;
  color: $white;
  padding: 0.5em 1em;
  border-radius: 0.125em;
  font-weight: $font-weight-bold;
  line-height: 1.125;
  text-align: center;
  margin-bottom: 0.25em;

  &[disabled] {
    background-color: $gray-base;
  }

  :deep() + button {
    margin-left: 0.25em;
  }

  :deep() > svg {
    width: 1em;
    height: 1em;
    vertical-align: -0.125em;
    margin-right: 0.25em;

    &:last-child {
      margin-right: 0;
    }
  }
}

.primary {
  background-color: $color-primary;

  &:hover:not([disabled]) {
    background-color: $color-primary-dark;
  }
}

.cta {
  background-color: $color-cta;
  color: $button-cta-text-color;

  &:hover:not([disabled]) {
    background-color: $color-cta-highlight;
  }
}

.inverted--bordered {
  box-shadow: inset 0 0 0 2px $color-primary;
  color: $color-primary;

  &:hover:not([disabled]) {
    box-shadow: inset 0 0 0 2px $color-primary-dark;
    color: $color-primary-dark;
  }
}

.secondary {
  background-color: transparent;
  color: var(--color-button-primary, var(--color-primary));
  transition: box-shadow 0.1s ease-in-out;

  &:hover:not([disabled]) {
    background-color: transparent;
    box-shadow: inset 0 0 0 2px var(--color-button-primary, var(--color-primary));
  }
}

.large {
  line-height: 1.5em;
  :deep() > svg {
    width: 1.5em;
    height: 1.5em;
    vertical-align: -0.5em;
  }
}

.search-action,
.action-primary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  padding: 11px 16px;
  border-radius: $border-radius-md;
  background: var(--color-primary);
  box-shadow: 0 1px 2px rgba(10, 42, 94, 0.25);
  transition: background 0.15s;

  &:hover:not([disabled]) {
    background: var(--color-primary-dark);
  }

  :deep() > svg {
    flex-shrink: 0;
    margin-right: 0;
  }
}

.search-action {
  width: 300px;
  max-width: 100%;
  text-align: left;

  :deep() > span {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.action-primary {
  justify-content: center;
}

.action-quiet {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  padding: 11px 16px;
  border-radius: $border-radius-md;
  border: 1.5px solid $gray-quiet-border;
  background: $white;
  color: $gray-quiet-text;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover:not([disabled]) {
    background: $gray-quiet-bg-hover;
    border-color: $gray-quiet-border-hover;
  }

  :deep() > svg {
    flex-shrink: 0;
    margin-right: 0;
  }
}

.danger {
  background-color: $color-danger;

  &:hover:not([disabled]) {
    background-color: color.adjust($color-danger, $lightness: -8%);
  }
}

.icon-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 64px;
  height: 64px;
  padding: 4px;
  border: 1px solid currentColor;
  border-radius: $border-radius-sm;
  background: transparent;
  color: $text-color;

  :deep(.btn-icon) {
    font-size: 30px;
    line-height: 1;
  }

  :deep(.btn-label) {
    font-size: 12px;
    line-height: 1;
  }

  &:hover:not([disabled]) {
    background-color: $gray-lightest;
  }

  &[disabled] {
    background-color: transparent;
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.theme-skoda {
  button:not(.icon-stack):not(.danger) {
    border-radius: 25px;

    &.searchButton {
      border-radius: 0.125em;
    }

    &:not([disabled]) {
      color: var(--color-primary);
      background-color: var(--color-primary-light);
    }

    &:hover:not([disabled]) {
      background-color: var(--color-primary-lighter);
    }
  }
}
</style>
